// ============================================================================
// CERTIFICATION DOMAIN API CLIENT
// ============================================================================
// Domain-specific API for compliance certifications, surveillance audits, and verification
// ============================================================================

import { ApiClient } from '@/core/apiClient';

export interface Certification {
  id: number;
  company_id: number;
  company_name?: string;
  framework_id: number;
  framework_name?: string;
  framework_version_id: number;
  certificate_number: string;
  issue_date: string;
  expiry_date: string;
  status: 'active' | 'expired' | 'revoked' | 'suspended';
  overall_score: number;
  auditor_id: number;
  auditor_name?: string;
  audit_report_url?: string;
  certificate_url?: string;
  verification_code: string;
  certification_type: 'initial' | 'renewal' | 'surveillance' | 'recertification';
  previous_certification_id?: number;
  created_at: string;
  updated_at: string;
}

export interface SurveillanceAudit {
  id: number;
  certification_id: number;
  audit_type: 'routine' | 'special' | 'follow_up';
  scheduled_date: string;
  completed_date?: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled';
  auditor_id: number;
  findings?: string;
  recommendations?: string;
  next_audit_date?: string;
  created_at: string;
  updated_at: string;
}

export interface CertificationStatistics {
  total_certifications: number;
  active_certifications: number;
  expired_certifications: number;
  revoked_certifications: number;
  average_score: number;
  certifications_by_framework: Array<{
    framework_id: number;
    framework_name: string;
    count: number;
  }>;
  certifications_by_month: Array<{
    month: string;
    count: number;
  }>;
}

export interface CreateCertificationRequest {
  companyId: number;
  frameworkId: number;
  frameworkVersionId: number;
  auditorId: number;
  certificationType?: 'initial' | 'renewal' | 'surveillance' | 'recertification';
  previousCertificationId?: number;
  auditReportUrl?: string;
}

export interface CreateSurveillanceAuditRequest {
  certificationId: number;
  auditType: 'routine' | 'special' | 'follow_up';
  scheduledDate: string;
  auditorId: number;
}

export interface CertificateVerificationResult {
  isValid: boolean;
  certification?: Certification;
  message: string;
}

/**
 * Certification Domain API Client
 * Encapsulates all certification-related API calls
 */
class CertificationApi extends ApiClient {
  constructor() {
    super({
      enableLogging: import.meta.env.DEV,
    });
  }

  // Certification Management
  async issueCertification(request: CreateCertificationRequest): Promise<Certification> {
    const response = await this.client.post('/compliance/certifications/issue', request);
    return response.data.data || response.data;
  }

  async getCertification(id: number): Promise<Certification> {
    const response = await this.client.get(`/compliance/certifications/${id}`);
    return response.data.data || response.data;
  }

  async getCompanyCertifications(companyId: number): Promise<Certification[]> {
    const response = await this.client.get(`/compliance/certifications/company/${companyId}`);
    return response.data.data || response.data || [];
  }

  async getAllCertifications(): Promise<Certification[]> {
    const response = await this.client.get('/compliance/certifications');
    return response.data.data || response.data || [];
  }

  async getActiveCertification(companyId: number, frameworkId: number): Promise<Certification | null> {
    try {
      const response = await this.client.get(`/compliance/certifications/active/${companyId}/${frameworkId}`);
      return response.data.data || response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        return null;
      }
      throw error;
    }
  }

  async revokeCertification(id: number, reason?: string): Promise<Certification> {
    const response = await this.client.post(`/compliance/certifications/${id}/revoke`, { reason });
    return response.data.data || response.data;
  }

  async suspendCertification(id: number, reason?: string): Promise<Certification> {
    const response = await this.client.post(`/compliance/certifications/${id}/suspend`, { reason });
    return response.data.data || response.data;
  }

  async reactivateCertification(id: number, reason?: string): Promise<Certification> {
    const response = await this.client.post(`/compliance/certifications/${id}/reactivate`, { reason });
    return response.data.data || response.data;
  }

  async renewCertification(id: number, auditReportUrl?: string): Promise<Certification> {
    const response = await this.client.post(`/compliance/certifications/${id}/renew`, { auditReportUrl });
    return response.data.data || response.data;
  }

  // Surveillance Audits
  async scheduleSurveillanceAudit(request: CreateSurveillanceAuditRequest): Promise<SurveillanceAudit> {
    const response = await this.client.post(`/compliance/certifications/${request.certificationId}/surveillance-audits`, request);
    return response.data.data || response.data;
  }

  async getSurveillanceAudits(certificationId: number): Promise<SurveillanceAudit[]> {
    const response = await this.client.get(`/compliance/certifications/${certificationId}/surveillance-audits`);
    return response.data.data || response.data || [];
  }

  async completeSurveillanceAudit(auditId: number, findings: string, recommendations?: string, nextAuditDate?: string): Promise<SurveillanceAudit> {
    const response = await this.client.post(`/compliance/certifications/surveillance-audits/${auditId}/complete`, {
      findings,
      recommendations,
      nextAuditDate
    });
    return response.data.data || response.data;
  }

  // Public Verification
  async verifyCertificate(verificationCode: string): Promise<CertificateVerificationResult> {
    const response = await this.client.get(`/compliance/certifications/verify/${verificationCode}`);
    return response.data.data || response.data;
  }

  // Statistics and Analytics
  async getCertificationStatistics(): Promise<CertificationStatistics> {
    const response = await this.client.get('/compliance/certifications/statistics');
    return response.data.data || response.data;
  }
}

// Export singleton instance
export const certificationApi = new CertificationApi();
export default certificationApi;